import React from 'react';
import moment from 'moment';
import { Paper, Stack, Typography } from '@mui/material';
import { DateFormats } from 'constants/common';
import { AttachmentsField } from './AttachmentsField';

interface TriggerField {
  label: string;
  getValue: (trigger: Record<string, unknown>) => string | null;
}

// Extend this array as new AI agent trigger shapes are introduced
const TRIGGER_FIELDS: TriggerField[] = [
  { label: 'Source', getValue: t => (t.email_from ? 'Email' : null) },
  { label: 'From', getValue: t => (t.email_from as string) ?? null },
  { label: 'To', getValue: t => (t.email_to as string) ?? null },
  { label: 'Subject', getValue: t => (t.email_subject as string) ?? null },
  {
    label: 'Received',
    getValue: t => (t.email_date ? moment(t.email_date as string).format(DateFormats.DateTimeShort12Hour) : null),
  },
  {
    label: 'Uploaded by',
    getValue: t => (t.user_uploaded_decision_letter ? (t.user_uploaded_decision_letter as string) : null),
  },
  { label: 'File', getValue: t => (t.file_name as string) ?? null },
  { label: 'Files Processed', getValue: t => (t.file_count != null ? String(t.file_count) : null) },
  {
    label: 'AI Overall Confidence',
    getValue: t =>
      t.orchestrator_confidence_score != null
        ? `${Math.round((t.orchestrator_confidence_score as number) * 100)}%`
        : null,
  },
  {
    label: 'AI Reasoning',
    getValue: t => (t.orchestrator_explanation as string) ?? null,
  },
];

interface TriggerDetailsSectionProps {
  trigger: Record<string, unknown> | null | undefined;
  payload?: Record<string, unknown> | null;
  agentActivityLogId?: string | null;
}

export const TriggerDetailsSection = ({ trigger, payload, agentActivityLogId }: TriggerDetailsSectionProps) => {
  if (!trigger) return null;

  const fields = TRIGGER_FIELDS.map(({ label, getValue }) => ({ label, value: getValue(trigger) })).filter(
    ({ value }) => value !== null
  );

  const attachments = Array.isArray(payload?.attachments)
    ? (payload!.attachments as Array<{ fileName: string; mimeType: string }>)
    : [];

  if (fields.length === 0 && attachments.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Trigger Details
      </Typography>
      <Stack spacing={1}>
        {fields.map(({ label, value }) => (
          <Stack key={label} direction="row" spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {value}
            </Typography>
          </Stack>
        ))}

        {attachments.length > 0 && agentActivityLogId && (
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, pt: 0.5 }}>
              Attachments
            </Typography>
            <AttachmentsField attachments={attachments} agentActivityLogId={agentActivityLogId} />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
